import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as XLSX from 'xlsx';
import './Motis.css';
import { startOfWeek, addDays, ymd, formatWeekRange } from '../utils/appointmentWeek';
import { buildMotisWorkbook } from '../utils/motisExport';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// MOTIS: installs sold by other channels. FSM uploads the week -> BAEs claim ->
// FSM passes leftovers to an R2 manager -> R2s claim. No points are awarded
// here; reps log MOTIS points in their daily log themselves.

const STATUS_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'unclaimed', label: 'Unclaimed' },
  { key: 'claimed', label: 'Claimed, not visited' },
  { key: 'visited', label: 'Visited' },
];

function statusOf(a) {
  if (a.visited) return 'visited';
  if (a.claimed_by) return 'claimed';
  return 'unclaimed';
}

const STATUS_LABEL = { unclaimed: 'Unclaimed', claimed: 'Claimed', visited: 'Visited' };

// "Oct 15" from "2026-10-15" without UTC shift.
function shortDate(s) {
  if (!s) return '';
  const [y, m, d] = s.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function noteStamp(s) {
  const d = new Date(s);
  if (isNaN(d)) return '';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function mapsUrl(a) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${a.address} ${a.zip_code}`)}`;
}

function matchesSearch(a, q) {
  if (!q) return true;
  const hay = `${a.account_name} ${a.address} ${a.zip_code} ${a.claimed_by_name} ${a.team_manager_name}`.toLowerCase();
  return hay.includes(q.toLowerCase());
}

// ── Spreadsheet upload helpers ──────────────────────────────────────────────

// For each field, patterns are tried in priority order. Tuned for the Salesforce
// MOTIS export: "Account" (not "Order: Order Name"), and "Opportunity: Location
// ID" — the full service address where the install happens — over the billing
// address. Fields are matched in this order so one column is never used twice.
const COLUMN_GUESSES = [
  ['zip_code', [/zip|postal/i]],
  ['install_date', [/install/i, /date/i]],
  ['arrival_window', [/arrival|window/i]],
  ['opp_number', [/opportunity number/i, /opp.*(number|#|num)/i]],
  ['contact_email', [/contact.*e-?mail/i, /e-?mail/i]],
  ['contact_phone', [/phone/i]],
  ['address', [/location id/i, /service addr/i, /addr|street/i]],
  ['contact_name', [/scheduling contact$/i, /contact name/i, /^contact$/i]],
  ['account_name', [/^account$/i, /account|customer|business|company/i, /name/i]],
];

const FIELD_LABELS = {
  account_name: 'Account Name',
  address: 'Address',
  zip_code: 'Zip Code',
  install_date: 'Install Date',
  arrival_window: 'Arrival Window',
  opp_number: 'Opp Number',
  contact_name: 'Scheduling Contact',
  contact_phone: 'Contact Phone',
  contact_email: 'Contact Email',
};

function guessColumns(headers) {
  const used = new Set();
  const mapping = {};
  for (const [field, patterns] of COLUMN_GUESSES) {
    let idx = -1;
    for (const re of patterns) {
      idx = headers.findIndex((h, i) => !used.has(i) && re.test(h)
        && (field === 'contact_email' || !/e-?mail/i.test(h)));
      if (idx >= 0) break;
    }
    mapping[field] = idx;
    if (idx >= 0) used.add(idx);
  }
  return mapping;
}

const pad2 = (n) => String(n).padStart(2, '0');

function cellToYMD(v) {
  if (v === '' || v == null) return '';
  if (v instanceof Date && !isNaN(v)) return ymd(v);
  if (typeof v === 'number') {
    const p = XLSX.SSF.parse_date_code(v);
    return p ? `${p.y}-${pad2(p.m)}-${pad2(p.d)}` : '';
  }
  const s = String(v).trim();
  // "2026-10-17" would parse as UTC midnight (the day before, in Florida).
  const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) return `${iso[1]}-${pad2(iso[2])}-${pad2(iso[3])}`;
  const d = new Date(s);
  return isNaN(d) ? '' : ymd(d);
}

function cellToZip(v) {
  if (typeof v === 'number') return String(v).padStart(5, '0');
  return String(v || '').trim();
}

// 8604609186 -> "(860) 460-9186"; anything not 10 digits is kept as typed.
function cellToPhone(v) {
  const s = String(v ?? '').trim();
  const digits = s.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
  return digits.length === 10 ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}` : s;
}

function buildRows(sheetRows, mapping) {
  const get = (r, field) => (mapping[field] >= 0 ? r[mapping[field]] : '');
  const text = (r, field) => String(get(r, field) ?? '').trim();
  return sheetRows
    .map((r) => ({
      account_name: text(r, 'account_name'),
      address: text(r, 'address'),
      zip_code: cellToZip(get(r, 'zip_code')),
      install_date: cellToYMD(get(r, 'install_date')),
      arrival_window: text(r, 'arrival_window'),
      opp_number: text(r, 'opp_number'),
      contact_name: text(r, 'contact_name'),
      contact_phone: cellToPhone(get(r, 'contact_phone')),
      contact_email: text(r, 'contact_email'),
    }))
    .filter((r) => r.account_name && r.address);
}

// ── Excel export ────────────────────────────────────────────────────────────

// The styling build of SheetJS is only fetched when someone exports.
async function exportWorkbook(appts, weekStart) {
  const mod = await import('xlsx-js-style');
  const XS = mod.default || mod;
  XS.writeFile(buildMotisWorkbook(XS, appts, formatWeekRange(weekStart)), `MOTIS_week_of_${ymd(weekStart)}.xlsx`);
}

// Keep in sync with MOTIS_OUTCOMES in models.py.
const OUTCOMES = [
  ['met_dm', 'Met decision maker'],
  ['left_card', 'Left card / no contact'],
  ['not_interested', 'Not interested'],
  ['follow_up', 'Follow-up scheduled'],
  ['opportunity', 'Opportunity created'],
  ['sale', 'Sale'],
];

function fromYMD(s) {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

// ── Component ───────────────────────────────────────────────────────────────

const Motis = ({ onBack, user }) => {
  const username = (user.username || '').toLowerCase();
  const isTrainer = !!user.is_trainer || username === 'msarka' || username === 'jcastillo';
  const canPick = username === 'acorvino'
    || (!isTrainer && (user.is_rep != null ? user.is_rep : !user.manages_people));
  const canManage = !!user.manages_people || !!user.is_director || !!user.is_senior_director;
  const canUpload = !!user.manages_people;

  const [tab, setTab] = useState(canPick ? 'pick' : 'team');
  // null = let the server open on the nearest week that has something to work
  // (e.g. next week's list uploaded during the current week).
  const [weekStart, setWeekStart] = useState(null);
  const skipNextLoad = useRef(false);
  const [repData, setRepData] = useState({ available: [], taken: [], mine: [], has_manager: true });
  const [teamData, setTeamData] = useState({ appointments: [], passable_count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [teamFilter, setTeamFilter] = useState('all');
  const [detailId, setDetailId] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const headers = { 'Content-Type': 'application/json', 'X-User-ID': String(user.userId) };

  // `silent` refreshes in place (no spinner, errors ignored) for the auto-refresh.
  const load = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setError('');
    }
    const url = tab === 'pick' ? '/api/motis/rep/' : '/api/motis/team/';
    const query = weekStart ? `?week=${ymd(weekStart)}` : '';
    try {
      const res = await fetch(`${API_URL}${url}${query}`, { headers });
      if (!res.ok) throw new Error();
      const data = await res.json();
      if (tab === 'pick') setRepData(data); else setTeamData(data);
      if (!weekStart) {
        // Adopt the week the server picked without fetching it a second time.
        skipNextLoad.current = true;
        setWeekStart(fromYMD(data.week_start));
      }
    } catch {
      if (!silent) setError('Failed to load MOTIS appointments.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [tab, weekStart, user.userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (skipNextLoad.current) {
      skipNextLoad.current = false;
      return;
    }
    load();
  }, [load]);

  const switchTab = (t) => {
    if (t === tab) return;
    setTab(t);
    setWeekStart(null);
  };

  // Reps' pick list refreshes every 30s so claims by teammates show up without
  // reloading. Paused while the app is in the background; catches up on return.
  useEffect(() => {
    if (tab !== 'pick') return undefined;
    const tick = () => { if (!document.hidden) load(true); };
    const id = setInterval(tick, 30000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [tab, load]);

  // Swap one updated appointment into whichever list holds it.
  const replaceAppt = (updated) => {
    setRepData((d) => ({
      ...d,
      mine: d.mine.map((a) => (a.id === updated.id ? updated : a)),
      available: d.available.map((a) => (a.id === updated.id ? updated : a)),
    }));
    setTeamData((d) => ({
      ...d,
      appointments: d.appointments.map((a) => (a.id === updated.id ? updated : a)),
    }));
  };

  const post = async (path, body) => {
    const res = await fetch(`${API_URL}/api/motis/${path}`, {
      method: 'POST', headers, body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Something went wrong');
    return data;
  };

  const claim = async (a) => {
    setBusyId(a.id);
    try {
      const updated = await post(`${a.id}/claim/`);
      setRepData((d) => ({
        ...d,
        available: d.available.filter((x) => x.id !== a.id),
        mine: [...d.mine, updated],
      }));
    } catch (e) {
      alert(e.message);
      load();
    } finally {
      setBusyId(null);
    }
  };

  const unclaim = async (a) => {
    const self = a.claimed_by === user.id || String(a.claimed_by) === String(user.id);
    const msg = self
      ? `Give up ${a.account_name}? It goes back to the pool for someone else to claim.`
      : `Release ${a.account_name} from ${a.claimed_by_name}? It goes back to the pool.`;
    if (!window.confirm(msg)) return;
    try {
      const updated = await post(`${a.id}/unclaim/`);
      if (tab === 'pick') {
        setDetailId(null);
        load();
      } else {
        replaceAppt(updated);
      }
    } catch (e) {
      alert(e.message);
    }
  };

  // outcome = one of OUTCOMES keys to mark visited (or change the outcome);
  // null to undo the visit.
  const setVisit = async (a, outcome) => {
    setBusyId(a.id);
    try {
      replaceAppt(await post(`${a.id}/visited/`, outcome ? { visited: true, outcome } : { visited: false }));
    } catch (e) {
      alert(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const addNote = async (a, text) => {
    replaceAppt(await post(`${a.id}/notes/`, { text }));
  };

  const changeNote = async (noteId, text) => {
    const res = await fetch(`${API_URL}/api/motis/notes/${noteId}/`, {
      method: text == null ? 'DELETE' : 'PUT', headers, body: text == null ? undefined : JSON.stringify({ text }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Could not update note');
    replaceAppt(data);
  };

  const deleteWeek = async () => {
    const n = teamData.my_upload_count;
    const claimed = teamData.appointments.filter((a) => String(a.uploaded_by) === String(user.id) && a.claimed_by).length;
    const msg = `Delete all ${n} appointments you uploaded for the week of ${formatWeekRange(weekStart)}?`
      + (claimed ? `\n\n${claimed} of them are already claimed — those claims and notes will be deleted too.` : '')
      + '\n\nAnything passed to R2 is removed as well. This cannot be undone.';
    if (!window.confirm(msg)) return;
    try {
      const data = await post('delete-week/', { week_start: ymd(weekStart) });
      alert(`Deleted ${data.deleted} appointment${data.deleted === 1 ? '' : 's'}.`);
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete ${a.account_name}? This removes it and its notes for everyone.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/motis/${a.id}/`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Delete failed');
      setDetailId(null);
      setTeamData((d) => ({ ...d, appointments: d.appointments.filter((x) => x.id !== a.id) }));
    } catch (e) {
      alert(e.message);
    }
  };

  const allLoaded = tab === 'pick' ? [...repData.mine, ...repData.available] : teamData.appointments;
  const detail = allLoaded.find((a) => a.id === detailId) || null;

  return (
    <div className="mo-container">
      <div className="mo-header">
        <button className="mo-back-btn" onClick={onBack}>Back</button>
        <h2>MOTIS</h2>
      </div>

      {canPick && canManage && (
        <div className="mo-tabs">
          <button className={`mo-tab ${tab === 'pick' ? 'active' : ''}`} onClick={() => switchTab('pick')}>My Picks</button>
          <button className={`mo-tab ${tab === 'team' ? 'active' : ''}`} onClick={() => switchTab('team')}>Team View</button>
        </div>
      )}

      {weekStart && (
        <div className="mo-week-nav">
          <button className="mo-nav-btn" onClick={() => setWeekStart((w) => addDays(w, -7))}>◀ Prev</button>
          <div className="mo-week-center">
            <div className="mo-week-range">Week of {formatWeekRange(weekStart)}</div>
            {ymd(weekStart) !== ymd(startOfWeek(new Date())) && (
              <button className="mo-link-btn" onClick={() => setWeekStart(startOfWeek(new Date()))}>This week</button>
            )}
          </div>
          <button className="mo-nav-btn" onClick={() => setWeekStart((w) => addDays(w, 7))}>Next ▶</button>
        </div>
      )}

      {error && <div className="mo-error">{error}</div>}

      {loading || !weekStart ? (
        <div className="mo-empty">Loading...</div>
      ) : tab === 'pick' ? (
        <PickView
          data={repData}
          search={search}
          setSearch={setSearch}
          busyId={busyId}
          onClaim={claim}
          onOpen={(a) => setDetailId(a.id)}
        />
      ) : (
        <TeamView
          data={teamData}
          weekStart={weekStart}
          canUpload={canUpload}
          search={search}
          setSearch={setSearch}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          teamFilter={teamFilter}
          setTeamFilter={setTeamFilter}
          onOpen={(a) => setDetailId(a.id)}
          onUpload={() => setShowUpload(true)}
          onPass={() => setShowPass(true)}
          onDeleteWeek={deleteWeek}
        />
      )}

      {detail && (
        <DetailModal
          appt={detail}
          user={user}
          mode={tab}
          busy={busyId === detail.id}
          onClose={() => setDetailId(null)}
          onAddNote={addNote}
          onChangeNote={changeNote}
          onSetVisit={setVisit}
          onUnclaim={unclaim}
          onDelete={remove}
        />
      )}

      {showUpload && (
        <UploadModal
          headers={headers}
          weekStart={weekStart || startOfWeek(new Date())}
          onClose={() => setShowUpload(false)}
          onDone={(week) => {
            setShowUpload(false);
            if (week !== ymd(weekStart)) setWeekStart(fromYMD(week));
            else load();
          }}
        />
      )}

      {showPass && (
        <PassModal
          user={user}
          headers={headers}
          weekStart={weekStart}
          count={teamData.passable_count}
          onClose={() => setShowPass(false)}
          onDone={() => { setShowPass(false); load(); }}
        />
      )}
    </div>
  );
};

// ── Rep: pick + work their claims ───────────────────────────────────────────

function PickView({ data, search, setSearch, busyId, onClaim, onOpen }) {
  // Open and teammate-claimed appointments in one zip-sorted list; claimed
  // ones are grayed out so reps can see what's gone and who took it.
  const taken = useMemo(() => data.taken || [], [data.taken]);
  const available = useMemo(
    () => [...data.available, ...taken]
      .filter((a) => matchesSearch(a, search))
      .sort((x, y) => (x.zip_code || '').localeCompare(y.zip_code || '') || x.account_name.localeCompare(y.account_name)),
    [data.available, taken, search]
  );

  if (!data.has_manager) {
    return <div className="mo-empty">You're not assigned to a manager yet. Ask your manager to add you to their team.</div>;
  }

  return (
    <>
      <h3 className="mo-section-title">My Appointments ({data.mine.length})</h3>
      {data.mine.length === 0 ? (
        <div className="mo-empty mo-empty-small">You haven't claimed any this week. Pick from the list below.</div>
      ) : (
        <div className="mo-card-list">
          {data.mine.map((a) => (
            <div key={a.id} className={`mo-card ${a.visited ? 'mo-card-visited' : ''}`}>
              <div className="mo-card-main" onClick={() => onOpen(a)}>
                <div className="mo-card-title">
                  {a.account_name}
                  {a.stage === 'R2' && <span className="mo-badge mo-badge-r2">R2</span>}
                </div>
                <a className="mo-address" href={mapsUrl(a)} target="_blank" rel="noopener noreferrer"
                   onClick={(e) => e.stopPropagation()}>
                  {a.address} {a.zip_code}
                </a>
                <div className="mo-card-meta">
                  {a.install_date && <span>Install {shortDate(a.install_date)} {a.arrival_window}</span>}
                  <span>{a.notes.length} note{a.notes.length === 1 ? '' : 's'} ›</span>
                </div>
                {(a.contact_name || a.contact_phone) && (
                  <div className="mo-card-meta">
                    <span>{a.contact_name}</span>
                    {a.contact_phone && (
                      <a className="mo-address" href={`tel:${a.contact_phone.replace(/[^\d+]/g, '')}`}
                         onClick={(e) => e.stopPropagation()}>
                        {a.contact_phone}
                      </a>
                    )}
                  </div>
                )}
              </div>
              {/* Opens the detail view, where the rep picks how the visit went. */}
              <button
                className={`mo-visit-btn ${a.visited ? 'done' : ''}`}
                disabled={busyId === a.id}
                onClick={() => onOpen(a)}
              >
                {a.visited ? `✓ ${a.outcome_label || 'Visited'}` : 'Mark Visited'}
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mo-section-head">
        <h3 className="mo-section-title">
          Available ({data.available.length}{taken.length > 0 && ` of ${data.available.length + taken.length}`})
        </h3>
        <input
          className="mo-search"
          placeholder="Search name, address, zip..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {available.length === 0 ? (
        <div className="mo-empty mo-empty-small">
          {data.available.length + taken.length === 0 ? 'Nothing to claim this week yet.' : 'No matches.'}
        </div>
      ) : (
        <div className="mo-pick-list">
          {available.map((a) => (
            <div key={a.id} className={`mo-pick-row ${a.claimed_by_name ? 'mo-pick-taken' : ''}`}>
              <div className="mo-pick-info">
                <div className="mo-card-title">
                  {a.account_name}
                  {a.stage === 'R2' && <span className="mo-badge mo-badge-r2">R2</span>}
                </div>
                <div className="mo-pick-sub">
                  {a.address} · <strong>{a.zip_code}</strong>
                  {a.install_date && <> · Install {shortDate(a.install_date)} {a.arrival_window}</>}
                </div>
              </div>
              {a.claimed_by_name ? (
                <span className="mo-taken-by">Claimed by {a.claimed_by_name}</span>
              ) : (
                <button className="mo-claim-btn" disabled={busyId === a.id} onClick={() => onClaim(a)}>
                  {busyId === a.id ? '...' : 'Claim'}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ── Manager / director: team table, upload, pass, export ───────────────────

function TeamView({
  data, weekStart, canUpload, search, setSearch, statusFilter, setStatusFilter,
  teamFilter, setTeamFilter, onOpen, onUpload, onPass, onDeleteWeek,
}) {
  const appts = data.appointments;
  const counts = useMemo(() => ({
    total: appts.length,
    unclaimed: appts.filter((a) => statusOf(a) === 'unclaimed').length,
    claimed: appts.filter((a) => a.claimed_by).length,
    visited: appts.filter((a) => a.visited).length,
    r2: appts.filter((a) => a.stage === 'R2').length,
  }), [appts]);

  const outcomeCounts = useMemo(() => {
    const c = {};
    appts.forEach((a) => { if (a.visited && a.outcome_label) c[a.outcome_label] = (c[a.outcome_label] || 0) + 1; });
    return Object.entries(c).sort((x, y) => y[1] - x[1]);
  }, [appts]);

  const teams = useMemo(
    () => [...new Set(appts.map((a) => a.team_manager_name))].sort((x, y) => x.localeCompare(y)),
    [appts]
  );

  const rows = useMemo(() => appts
    .filter((a) => statusFilter === 'all' || statusOf(a) === statusFilter)
    .filter((a) => teamFilter === 'all' || a.team_manager_name === teamFilter)
    .filter((a) => matchesSearch(a, search))
    .sort((x, y) => x.team_manager_name.localeCompare(y.team_manager_name)
      || (x.install_date || '').localeCompare(y.install_date || '')
      || x.account_name.localeCompare(y.account_name)),
  [appts, statusFilter, teamFilter, search]);

  return (
    <>
      <div className="mo-actions">
        {canUpload && <button className="mo-primary-btn" onClick={onUpload}>Upload Spreadsheet</button>}
        {canUpload && data.passable_count > 0 && (
          <button className="mo-warn-btn" onClick={onPass}>
            Pass {data.passable_count} Unclaimed to R2
          </button>
        )}
        <button className="mo-secondary-btn" disabled={appts.length === 0}
                onClick={() => exportWorkbook(appts, weekStart).catch(() => alert('Export failed. Try again.'))}>
          Export to Excel
        </button>
        {data.my_upload_count > 0 && (
          <button className="mo-link-btn mo-delete-week" onClick={onDeleteWeek}>
            Delete this week's upload
          </button>
        )}
      </div>

      <div className="mo-stats">
        <Stat label="Total" value={counts.total} />
        <Stat label="Unclaimed" value={counts.unclaimed} />
        <Stat label="Claimed" value={counts.claimed} />
        <Stat label="Visited" value={counts.visited} tone="success" />
        <Stat label="Passed to R2" value={counts.r2} tone="warning" />
      </div>

      {outcomeCounts.length > 0 && (
        <div className="mo-outcomes">
          <span className="mo-muted">Outcomes:</span>
          {outcomeCounts.map(([label, n]) => (
            <span key={label} className="mo-outcome-pill">{label} <strong>{n}</strong></span>
          ))}
        </div>
      )}

      {appts.length === 0 ? (
        <div className="mo-empty">
          No MOTIS appointments for this week.{canUpload && ' Upload the spreadsheet to get started.'}
        </div>
      ) : (
        <>
          <div className="mo-filters">
            {STATUS_FILTERS.map((f) => (
              <button key={f.key} className={`mo-chip ${statusFilter === f.key ? 'active' : ''}`}
                      onClick={() => setStatusFilter(f.key)}>
                {f.label}
              </button>
            ))}
            {teams.length > 1 && (
              <select className="mo-select" value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)}>
                <option value="all">All managers</option>
                {teams.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            )}
            <input className="mo-search" placeholder="Search..." value={search}
                   onChange={(e) => setSearch(e.target.value)} />
          </div>

          <div className="mo-table-wrap">
            <table className="mo-table">
              <thead>
                <tr>
                  <th>Account</th>
                  <th className="mo-hide-sm">Zip</th>
                  <th className="mo-hide-sm">Install</th>
                  <th>Manager</th>
                  <th>Claimed By</th>
                  <th>Status</th>
                  <th className="mo-hide-sm">Notes</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => {
                  const st = statusOf(a);
                  return (
                    <tr key={a.id} onClick={() => onOpen(a)}>
                      <td>
                        <div className="mo-cell-title">{a.account_name}</div>
                        <div className="mo-cell-sub">{a.address}</div>
                      </td>
                      <td className="mo-hide-sm">{a.zip_code}</td>
                      <td className="mo-hide-sm">
                        {shortDate(a.install_date)}
                        {a.arrival_window && <div className="mo-cell-sub">{a.arrival_window}</div>}
                      </td>
                      <td>
                        {a.team_manager_name}
                        <span className={`mo-badge ${a.stage === 'R2' ? 'mo-badge-r2' : 'mo-badge-bae'}`}>{a.stage}</span>
                      </td>
                      <td>{a.claimed_by_name || <span className="mo-muted">—</span>}</td>
                      <td>
                        <span className={`mo-status mo-status-${st}`}>{STATUS_LABEL[st]}</span>
                        {a.outcome_label && <div className="mo-cell-sub">{a.outcome_label}</div>}
                      </td>
                      <td className="mo-hide-sm">{a.notes.length || ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {rows.length === 0 && <div className="mo-empty mo-empty-small">No appointments match these filters.</div>}
          </div>
        </>
      )}
    </>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className={`mo-stat ${tone ? `mo-stat-${tone}` : ''}`}>
      <div className="mo-stat-value">{value}</div>
      <div className="mo-stat-label">{label}</div>
    </div>
  );
}

// ── Appointment detail + notes timeline ─────────────────────────────────────

function DetailModal({
  appt, user, mode, busy, onClose, onAddNote, onChangeNote, onSetVisit, onUnclaim, onDelete,
}) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const isMine = String(appt.claimed_by) === String(user.id);
  const isUploader = String(appt.uploaded_by) === String(user.id);
  const st = statusOf(appt);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSaving(true);
    setErr('');
    try {
      await onAddNote(appt, text.trim());
      setText('');
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    if (!editText.trim()) return;
    setErr('');
    try {
      await onChangeNote(editingId, editText.trim());
      setEditingId(null);
    } catch (e2) {
      setErr(e2.message);
    }
  };

  const deleteNote = async (n) => {
    if (!window.confirm('Delete this note?')) return;
    setErr('');
    try {
      await onChangeNote(n.id, null);
    } catch (e2) {
      setErr(e2.message);
    }
  };

  return (
    <div className="mo-modal-overlay" onClick={onClose}>
      <div className="mo-modal" onClick={(e) => e.stopPropagation()}>
        <button className="mo-modal-close" onClick={onClose}>×</button>
        <h3>{appt.account_name}</h3>
        <a className="mo-address" href={mapsUrl(appt)} target="_blank" rel="noopener noreferrer">
          {appt.address} {appt.zip_code}
        </a>

        <div className="mo-detail-grid">
          <span>Install</span>
          <span>{appt.install_date ? shortDate(appt.install_date) : '—'} {appt.arrival_window}</span>
          {appt.opp_number && <><span>Opp #</span><span>{appt.opp_number}</span></>}
          {appt.contact_name && <><span>Contact</span><span>{appt.contact_name}</span></>}
          {appt.contact_phone && (
            <><span>Phone</span>
              <span><a className="mo-address" href={`tel:${appt.contact_phone.replace(/[^\d+]/g, '')}`}>{appt.contact_phone}</a></span></>
          )}
          {appt.contact_email && (
            <><span>Email</span>
              <span><a className="mo-address" href={`mailto:${appt.contact_email}`}>{appt.contact_email}</a></span></>
          )}
          <span>Status</span><span><span className={`mo-status mo-status-${st}`}>{STATUS_LABEL[st]}</span></span>
          <span>Claimed by</span><span>{appt.claimed_by_name || '—'}</span>
          {appt.visited && (
            <><span>Visited</span><span>{shortDate(appt.visited_date)} · {appt.outcome_label || '—'}</span></>
          )}
          <span>Uploaded by</span><span>{appt.uploaded_by_name}</span>
          {appt.stage === 'R2' && <><span>Passed to R2</span><span>{appt.passed_to_name}</span></>}
        </div>

        {isMine && (
          <div className="mo-outcome-picker">
            <div className="mo-label">
              {appt.visited ? 'How it went (tap to change)' : 'Visited? Tap how it went:'}
            </div>
            <div className="mo-outcome-options">
              {OUTCOMES.map(([key, label]) => (
                <button
                  key={key}
                  className={`mo-chip ${appt.outcome === key ? 'active' : ''}`}
                  disabled={busy}
                  onClick={() => appt.outcome !== key && onSetVisit(appt, key)}
                >
                  {label}
                </button>
              ))}
            </div>
            {appt.visited && (
              <button className="mo-link-btn" disabled={busy} onClick={() => onSetVisit(appt, null)}>
                Undo visit
              </button>
            )}
          </div>
        )}

        <div className="mo-modal-actions">
          {appt.claimed_by && (isMine || mode === 'team') && (
            <button className="mo-secondary-btn" onClick={() => onUnclaim(appt)}>
              {isMine ? 'Unclaim' : 'Release Claim'}
            </button>
          )}
          {mode === 'team' && isUploader && (
            <button className="mo-danger-btn" onClick={() => onDelete(appt)}>Delete</button>
          )}
        </div>

        <h4 className="mo-notes-title">Notes</h4>
        {appt.notes.length === 0 ? (
          <div className="mo-muted">No notes yet.</div>
        ) : (
          <ul className="mo-notes">
            {appt.notes.map((n) => {
              const own = String(n.author) === String(user.id);
              return (
                <li key={n.id}>
                  <div className="mo-note-meta">
                    {n.author_name} · {noteStamp(n.created_at)}{n.edited_at && ' (edited)'}
                    {own && editingId !== n.id && (
                      <span className="mo-note-actions">
                        <button className="mo-link-btn" onClick={() => { setEditingId(n.id); setEditText(n.text); }}>Edit</button>
                        <button className="mo-link-btn mo-link-danger" onClick={() => deleteNote(n)}>Delete</button>
                      </span>
                    )}
                  </div>
                  {editingId === n.id ? (
                    <div className="mo-note-form">
                      <textarea className="mo-input" rows={3} value={editText}
                                onChange={(e) => setEditText(e.target.value)} />
                      <div className="mo-modal-actions">
                        <button className="mo-secondary-btn" onClick={() => setEditingId(null)}>Cancel</button>
                        <button className="mo-primary-btn" disabled={!editText.trim()} onClick={saveEdit}>Save</button>
                      </div>
                    </div>
                  ) : (
                    <div className="mo-note-text">{n.text}</div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {(isMine || mode === 'team') && (
          <form onSubmit={submit} className="mo-note-form">
            <textarea
              className="mo-input"
              rows={3}
              placeholder="What happened? Who did you talk to? Next steps..."
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            {err && <div className="mo-form-error">{err}</div>}
            <button type="submit" className="mo-primary-btn" disabled={saving || !text.trim()}>
              {saving ? 'Saving...' : 'Add Note'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Upload ──────────────────────────────────────────────────────────────────

function UploadModal({ headers, weekStart, onClose, onDone }) {
  const [week, setWeek] = useState(ymd(weekStart));
  const [fileName, setFileName] = useState('');
  const [sheetHeaders, setSheetHeaders] = useState([]);
  const [sheetRows, setSheetRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const rows = useMemo(() => buildRows(sheetRows, mapping), [sheetRows, mapping]);

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setErr('');
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        // Dates stay as Excel serial numbers (converted in cellToYMD) — cellDates
        // can land a day early depending on the browser's timezone.
        // raw: CSV text cells stay strings instead of being date-guessed in UTC.
        const wb = XLSX.read(new Uint8Array(evt.target.result), { type: 'array', raw: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const all = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: true });
        // Header row = first row with at least two filled cells (skips title rows).
        const hi = all.findIndex((r) => r.filter((c) => String(c).trim() !== '').length >= 2);
        if (hi < 0) throw new Error('empty');
        const hdrs = all[hi].map((h) => String(h).trim());
        const guessed = guessColumns(hdrs);
        setSheetHeaders(hdrs);
        setSheetRows(all.slice(hi + 1));
        setMapping(guessed);
        // Preselect the week most installs fall in (the export often starts
        // on a Sunday, which belongs to the previous Mon-based week).
        const perWeek = {};
        for (const r of buildRows(all.slice(hi + 1), guessed)) {
          if (!r.install_date) continue;
          const [y, m, d] = r.install_date.split('-').map(Number);
          const wk = ymd(startOfWeek(new Date(y, m - 1, d)));
          perWeek[wk] = (perWeek[wk] || 0) + 1;
        }
        const best = Object.keys(perWeek).sort((a, b) => perWeek[b] - perWeek[a])[0];
        if (best) setWeek(best);
      } catch {
        setErr('Could not read that file. Upload an .xlsx, .xls, or .csv spreadsheet.');
        setSheetHeaders([]);
        setSheetRows([]);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const submit = async () => {
    if (mapping.account_name < 0 || mapping.address < 0) {
      setErr('Pick which columns hold the Account Name and Address.');
      return;
    }
    if (rows.length === 0) {
      setErr('No rows with both an account name and address were found.');
      return;
    }
    setSaving(true);
    setErr('');
    try {
      const res = await fetch(`${API_URL}/api/motis/upload/`, {
        method: 'POST', headers, body: JSON.stringify({ week_start: week, rows }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Upload failed');
      alert(`Added ${data.created} appointment${data.created === 1 ? '' : 's'}`
        + (data.skipped ? ` (${data.skipped} skipped: duplicates or blank rows).` : '.'));
      onDone(data.week_start);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mo-modal-overlay" onClick={onClose}>
      <div className="mo-modal mo-modal-wide" onClick={(e) => e.stopPropagation()}>
        <button className="mo-modal-close" onClick={onClose}>×</button>
        <h3>Upload MOTIS Spreadsheet</h3>

        <label className="mo-label">Week these appointments are for</label>
        <input type="date" className="mo-input" value={week} onChange={(e) => setWeek(e.target.value)} />
        <div className="mo-hint">Any day in the week works; it's saved to that week's Monday.</div>

        <label className="mo-label">Spreadsheet</label>
        <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} className="mo-file" />
        {fileName && <div className="mo-hint">{fileName}</div>}

        {sheetHeaders.length > 0 && (
          <>
            <div className="mo-mapping">
              {Object.keys(FIELD_LABELS).map((field) => (
                <div key={field} className="mo-map-row">
                  <span>{FIELD_LABELS[field]}</span>
                  <select className="mo-select" value={mapping[field] ?? -1}
                          onChange={(e) => setMapping({ ...mapping, [field]: Number(e.target.value) })}>
                    <option value={-1}>— not in file —</option>
                    {sheetHeaders.map((h, i) => <option key={i} value={i}>{h || `Column ${i + 1}`}</option>)}
                  </select>
                </div>
              ))}
            </div>

            <div className="mo-label">Preview ({rows.length} appointment{rows.length === 1 ? '' : 's'})</div>
            <div className="mo-table-wrap">
              <table className="mo-table mo-table-preview">
                <thead>
                  <tr><th>Account</th><th>Address</th><th>Zip</th><th>Install</th><th>Contact</th></tr>
                </thead>
                <tbody>
                  {rows.slice(0, 5).map((r, i) => (
                    <tr key={i}>
                      <td>{r.account_name}<div className="mo-cell-sub">{r.opp_number}</div></td>
                      <td>{r.address}</td><td>{r.zip_code}</td>
                      <td>{shortDate(r.install_date)}<div className="mo-cell-sub">{r.arrival_window}</div></td>
                      <td>{r.contact_name}<div className="mo-cell-sub">{r.contact_phone}</div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {err && <div className="mo-form-error">{err}</div>}

        <div className="mo-modal-actions">
          <button className="mo-secondary-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="mo-primary-btn" onClick={submit} disabled={saving || rows.length === 0}>
            {saving ? 'Uploading...' : `Upload ${rows.length || ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Pass leftovers to R2 ────────────────────────────────────────────────────

function PassModal({ user, headers, weekStart, count, onClose, onDone }) {
  const storageKey = `motisLastR2_${user.id}`;
  const [managers, setManagers] = useState([]);
  const [choice, setChoice] = useState(() => {
    try { return localStorage.getItem(storageKey) || ''; } catch { return ''; }
  });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/motis/r2-managers/`, { headers })
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => {
        setManagers(list);
        setChoice((c) => (list.some((m) => String(m.id) === c) ? c : ''));
      })
      .catch(() => setErr('Could not load managers.'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!choice) { setErr('Choose an R2 manager.'); return; }
    const m = managers.find((x) => String(x.id) === choice);
    const name = m ? (m.full_name || m.username) : 'this manager';
    if (!window.confirm(`Pass ${count} unclaimed appointment${count === 1 ? '' : 's'} to ${name}? Your BAEs will no longer be able to claim them.`)) return;
    setSaving(true);
    setErr('');
    try {
      const res = await fetch(`${API_URL}/api/motis/pass/`, {
        method: 'POST', headers, body: JSON.stringify({ week_start: ymd(weekStart), r2_manager_id: Number(choice) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Pass failed');
      try { localStorage.setItem(storageKey, choice); } catch { /* per-device convenience only */ }
      alert(`Passed ${data.passed} to ${data.to}.`);
      onDone();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mo-modal-overlay" onClick={onClose}>
      <div className="mo-modal" onClick={(e) => e.stopPropagation()}>
        <button className="mo-modal-close" onClick={onClose}>×</button>
        <h3>Pass to R2</h3>
        <p className="mo-hint">
          Sends the {count} appointment{count === 1 ? '' : 's'} your BAEs didn't claim this week to an R2 manager.
          Their R2s can then claim them. Claimed appointments stay with your team.
        </p>
        <label className="mo-label">R2 manager</label>
        <select className="mo-select mo-input" value={choice} onChange={(e) => setChoice(e.target.value)}>
          <option value="">Select a manager...</option>
          {managers.map((m) => <option key={m.id} value={m.id}>{m.full_name || m.username}</option>)}
        </select>
        {err && <div className="mo-form-error">{err}</div>}
        <div className="mo-modal-actions">
          <button className="mo-secondary-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="mo-warn-btn" onClick={submit} disabled={saving || !choice}>
            {saving ? 'Passing...' : `Pass ${count}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Motis;
